import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { AssimagraMetrics } from "../../types/assimagra/assimagra-metrics.types";
import { useLanguage } from "../../contexts/LanguageContext";

interface StoneTypePieChartProps {
  metrics: AssimagraMetrics | null;
}

export const StoneTypePieChart = ({ metrics }: StoneTypePieChartProps) => {
  const { t } = useLanguage();

  // Provide default values if metrics is null
  const safeMetrics = metrics || {
    stone_types: { limestone: 0, marble: 0, granite: 0, schist: 0, other: 0 },
  };

  // Prepare data for the pie chart
  const stoneData = [
    {
      name: t("assimagraDashboard.charts.stoneTypes.limestone"),
      nameKey: "limestone",
      value: safeMetrics.stone_types.limestone,
      color: "#8B5CF6",
    },
    {
      name: t("assimagraDashboard.charts.stoneTypes.marble"),
      nameKey: "marble",
      value: safeMetrics.stone_types.marble,
      color: "#06B6D4",
    },
    {
      name: t("assimagraDashboard.charts.stoneTypes.granite"),
      nameKey: "granite",
      value: safeMetrics.stone_types.granite,
      color: "#10B981",
    },
    {
      name: t("assimagraDashboard.charts.stoneTypes.schist"),
      nameKey: "schist",
      value: safeMetrics.stone_types.schist,
      color: "#F59E0B",
    },
    {
      name: t("assimagraDashboard.charts.stoneTypes.other"),
      nameKey: "other",
      value: safeMetrics.stone_types.other,
      color: "#6B7280",
    },
  ].filter((item) => item.value > 0); // Only show stone types with data

  const totalStones = stoneData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage =
        totalStones > 0 ? ((data.value / totalStones) * 100).toFixed(1) : "0";
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.payload.name}</p>
          <p className="text-sm text-gray-600">
            {t("assimagraDashboard.charts.stoneTypes.tooltip.count")}:{" "}
            <span className="font-medium">{data.value}</span>
          </p>
          <p className="text-sm text-gray-600">
            {t("assimagraDashboard.charts.stoneTypes.tooltip.percentage")}:{" "}
            <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap justify-center gap-2 mt-2">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-600">
              {entry.value} ({entry.payload.value})
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("assimagraDashboard.charts.stoneTypes.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("assimagraDashboard.charts.stoneTypes.subtitle", {
            total: totalStones,
          })}
        </p>
      </CardHeader>
      <CardContent>
        {totalStones > 0 ? (
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stoneData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {stoneData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-72 text-gray-500">
            <div className="text-center">
              <p className="text-base mb-1">
                {t("assimagraDashboard.charts.stoneTypes.noData")}
              </p>
              <p className="text-sm">
                {t("assimagraDashboard.charts.stoneTypes.description")}
              </p>
            </div>
          </div>
        )}
      </CardContent>
      {totalStones > 0 && (
        <CardFooter className="flex justify-center">
          <CustomLegend
            payload={stoneData.map((d) => ({
              value: d.name,
              color: d.color,
              payload: { value: d.value },
            }))}
          />
        </CardFooter>
      )}
    </Card>
  );
};
